/** @jsxImportSource @emotion/react */

import { useLazyQuery, useMutation } from '@apollo/client'
import { Modal } from '@mui/material'
import IconButton from '@mui/material/IconButton'
import Typography from '@mui/material/Typography'
import { FC, useCallback, useEffect, useState } from 'react'
import InsertLinkIcon from '@mui/icons-material/InsertLink'
import { TAKE_ALL_ITEMS_COUNT } from 'constants/consts'
import { bulkRemoveCheatSheet, bulkRemoveCheatSheetVariables } from '__generatedTypes__/bulkRemoveCheatSheet'
import {
  findAllCheatSheet,
  findAllCheatSheet_findAllCheatSheet_items as ICheatSheet,
  findAllCheatSheetVariables,
} from '__generatedTypes__/findAllCheatSheet'
import { findAllCheatSheetGroup_findAllCheatSheetGroup_items as IGroup } from '__generatedTypes__/findAllCheatSheetGroup'
import { ReactComponent as EditIcon } from 'assets/icons/edit.svg'
import Badge from 'components/Badge'
import DeleteModal from 'components/Modals/DeleteModal'
import Table, { TableProps } from 'components/Table'
import TableButtons from 'components/TableButtons/TableButtons'
import client from 'configs/apolloClient'
import { BULK_REMOVE_CHEAT_SHEET, BULK_UPDATE_CHEAT_SHEET, FIND_ALL_CHEAT_SHEET } from 'queries/cheatSheet'
import { bulkUpdateCheatSheet, bulkUpdateCheatSheetVariables } from '__generatedTypes__/bulkUpdateCheatSheet'
import { toast } from 'utils/toast'
import * as commonClasses from 'components/shared/GroupCards/commonStyles'
import { usePlatformsAndSubjects } from 'hooks/usePlatformsAndSubjects'
import { isLandingPageAvailable } from '../platformConfig'
import ManageCheatSheetsModal from './ManageCheatSheetsModal'
import * as classes from './styles'

interface IProps {
  groups: IGroup[]
  initialSelected: IGroup
  selectedSubjectId: string
}

const CheatSheetsTabs: FC<IProps> = ({ groups, selectedSubjectId, initialSelected }) => {
  const { selectedPlatformSlug } = usePlatformsAndSubjects({})
  const hasLandingPage = isLandingPageAvailable(selectedPlatformSlug)
  const [getCheatSheets, { data: cheatSheetData }] = useLazyQuery<findAllCheatSheet, findAllCheatSheetVariables>(
    FIND_ALL_CHEAT_SHEET
  )

  const [cheatSheets, setCheatSheets] = useState<ICheatSheet[]>([])
  const [selectedGroup, setSelectedGroup] = useState<IGroup>(initialSelected)
  const [isCardModalOpen, setCardModalOpen] = useState<boolean>(false)
  const [isBulkDeleteModalOpen, setBulkDeleteModalOpen] = useState<boolean>(false)
  const [selectedCheatSheetCard, setSelectedCheatSheetCard] = useState<ICheatSheet | null>(null)
  const [cheatSheetIdsToDelete, setCheatSheetIdsToDelete] = useState<ICheatSheet[]>([])

  const [bulkUpdateCheatSheet] = useMutation<bulkUpdateCheatSheet, bulkUpdateCheatSheetVariables>(
    BULK_UPDATE_CHEAT_SHEET
  )
  const [bulkRemoveCheatSheet] = useMutation<bulkRemoveCheatSheet, bulkRemoveCheatSheetVariables>(
    BULK_REMOVE_CHEAT_SHEET
  )

  useEffect(() => {
    setCheatSheets([])
  }, [selectedSubjectId])

  useEffect(() => {
    setCheatSheetIdsToDelete([])
    selectedGroup &&
      getCheatSheets({
        variables: {
          query: {
            take: TAKE_ALL_ITEMS_COUNT,
            where: {
              cheatSheetGroup: {
                id: selectedGroup.id,
              },
            },
          },
        },
      })
  }, [selectedGroup])

  useEffect(() => {
    cheatSheetData && setCheatSheets(cheatSheetData.findAllCheatSheet.items || [])
  }, [cheatSheetData])

  useEffect(() => {
    if (selectedGroup && !groups.find((group) => group.id === selectedGroup.id)) {
      setSelectedGroup(groups[0])
      setCheatSheets([])
    }
  }, [groups])

  const handleModalClose = () => {
    setSelectedCheatSheetCard(null)
    setCardModalOpen(false)
  }

  const handleEditClick = (cheatSheet: ICheatSheet) => {
    setSelectedCheatSheetCard(cheatSheet)
    setCardModalOpen(true)
    setCheatSheetIdsToDelete([])
  }

  const handleBulkDelete = useCallback(async () => {
    try {
      const ids = cheatSheetIdsToDelete.map((group) => ({ id: group.id }))
      await bulkRemoveCheatSheet({ variables: { ids } })
      setCheatSheetIdsToDelete([])
      setBulkDeleteModalOpen(false)
      client.refetchQueries({
        include: [FIND_ALL_CHEAT_SHEET],
      })
      toast.success('Cards deleted successfully')
    } catch (error) {
      toast.error('Failed to delete cards')
    }
  }, [cheatSheetIdsToDelete])

  const CheatSheetColumn: TableProps<ICheatSheet>['columns'] = [
    {
      title: 'Title',
      renderCell: (note) => (
        <Typography css={commonClasses.longText} variant="body2" data-testid="card-title">
          {note.title}
        </Typography>
      ),
      flex: '0 1 300px',
    },
    {
      title: hasLandingPage ? 'Classroom Description' : 'Description',
      renderCell: (note) => (
        <Typography css={commonClasses.longText} variant="body2">
          {note.description}
        </Typography>
      ),
      flex: '0 1 300px',
    },
    ...(hasLandingPage
      ? [
          {
            title: 'Landing Page Description',
            renderCell: (note: ICheatSheet) => (
              <Typography css={commonClasses.longText} variant="body2">
                {note.landingPageDescription || '-'}
              </Typography>
            ),
            flex: '0 1 300px',
          },
        ]
      : []),
    {
      title: 'Group',
      renderCell: (note) => (
        <Badge text={note.cheatSheetGroup.title} color={note.cheatSheetGroup.color} css={classes.badge} />
      ),
      flex: '0 1 250px',
    },
    {
      title: 'URL',
      renderCell: (note) => (
        <IconButton size="small" href={note.link} target="_blank" css={classes.link}>
          <InsertLinkIcon />
        </IconButton>
      ),
      flex: '0 1 100px',
    },
    {
      renderCell: (note) => (
        <EditIcon css={commonClasses.editIcon} onClick={() => handleEditClick(note)} data-testid="edit-card-btn" />
      ),
      flex: '0 1 29px',
    },
  ]

  const onSort = useCallback(async (items: ICheatSheet[]) => {
    try {
      if (items.length > 0) {
        setCheatSheets(items)

        await bulkUpdateCheatSheet({
          variables: {
            params: items.map(({ id }, order) => ({ id, order })),
          },
        })

        toast.success('Cards order updated successfully')
      }
    } catch (error) {
      toast.error('Failed to update cards order')
    }
  }, [])

  return (
    <div>
      <Typography css={classes.title} variant="title3" component="p">
        Cards
      </Typography>
      <div css={classes.tabsBlock}>
        {groups.map((group) => (
          <Typography
            onClick={() => setSelectedGroup(group)}
            key={group.id}
            css={[classes.tab, selectedGroup?.id === group.id && classes.activeTab]}
            variant="body2"
          >
            {group.title}
          </Typography>
        ))}
      </div>
      <Table
        columns={CheatSheetColumn}
        items={cheatSheets}
        isSelectable
        selectedItems={cheatSheetIdsToDelete}
        onSelect={setCheatSheetIdsToDelete}
        onSort={onSort}
        isSortable
      />
      {selectedGroup && (
        <TableButtons
          itemName="Card"
          areItemsToDelete={cheatSheetIdsToDelete.length !== 0}
          onAddItem={() => setCardModalOpen(true)}
          onCancel={() => setCheatSheetIdsToDelete([])}
          onBatchDelete={() => setBulkDeleteModalOpen(true)}
        />
      )}
      {selectedGroup !== null && (
        <Modal open={isCardModalOpen} onClose={handleModalClose}>
          <ManageCheatSheetsModal
            isEditMode={!!selectedCheatSheetCard}
            onClose={handleModalClose}
            cheatSheetGroups={groups}
            cheatSheet={selectedCheatSheetCard ?? { id: 'id', cheatSheetGroup: { id: selectedGroup.id } }}
            cheatSheetLength={cheatSheets.length}
          />
        </Modal>
      )}
      <DeleteModal
        open={isBulkDeleteModalOpen}
        count={cheatSheetIdsToDelete.length}
        onClose={() => setBulkDeleteModalOpen(false)}
        onConfirm={handleBulkDelete}
      />
    </div>
  )
}

export default CheatSheetsTabs
